import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Search, Film, MessageSquare } from 'lucide-react';
import MovieCard from '@/components/MovieCard';
import { formatDistanceToNow } from 'date-fns';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function SearchResults() {
  const [searchParams] = useSearchParams();
  const query = searchParams.get('q') || '';
  const [results, setResults] = useState({ movies: [], threads: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (query) {
      performSearch();
    }
  }, [query]);

  const performSearch = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/search?q=${encodeURIComponent(query)}`);
      setResults(res.data);
    } catch (error) {
      console.error('Search error:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center pt-20"><div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div></div>;
  }

  const hasResults = results.movies.length > 0 || results.threads.length > 0;

  return (
    <div className="min-h-screen pt-20" data-testid="search-results-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="mb-8">
          <h1 className="font-heading text-4xl font-bold tracking-tight mb-2">Search Results</h1>
          <p className="text-muted-foreground">Showing results for "{query}"</p>
        </div>

        {!hasResults ? (
          <div className="text-center py-20"><Search className="w-16 h-16 text-muted-foreground mx-auto mb-4" /><p className="text-xl text-muted-foreground">No results found for "{query}"</p></div>
        ) : (
          <div className="space-y-12">
            {results.movies.length > 0 && (
              <section>
                <div className="flex items-center space-x-3 mb-6"><Film className="w-6 h-6 text-primary" /><h2 className="font-heading text-2xl font-bold">Movies ({results.movies.length})</h2></div>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-6">
                  {results.movies.map(movie => <MovieCard key={movie.id} movie={movie} />)}
                </div>
              </section>
            )}

            {results.threads.length > 0 && (
              <section>
                <div className="flex items-center space-x-3 mb-6"><MessageSquare className="w-6 h-6 text-primary" /><h2 className="font-heading text-2xl font-bold">Community Threads ({results.threads.length})</h2></div>
                <div className="space-y-4">
                  {results.threads.map(thread => (
                    <Link key={thread.thread_id} to={`/community/${thread.thread_id}`} className="block bg-card border border-border rounded-lg p-6 hover:border-primary transition-colors">
                      <div className="flex items-center space-x-3 mb-2"><span className="text-xs px-3 py-1 bg-primary/20 text-primary rounded-full">{thread.category}</span><span className="text-sm text-muted-foreground">{thread.user_name}</span><span className="text-sm text-muted-foreground">{formatDistanceToNow(new Date(thread.created_at), { addSuffix: true })}</span></div>
                      <h3 className="font-heading text-xl font-bold mb-2">{thread.title}</h3>
                      {thread.content && <p className="text-muted-foreground line-clamp-2">{thread.content}</p>}
                      <div className="flex items-center space-x-2 text-muted-foreground mt-3"><MessageSquare className="w-4 h-4" /><span className="text-sm">{thread.comment_count} comments</span></div>
                    </Link>
                  ))}
                </div>
              </section>
            )}
          </div>
        )}
      </div>
    </div>
  );
}